import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, ShieldCheck, LogOut, ExternalLink, Menu, X, Users, Store, Radio, Package, Megaphone, ShoppingCart, Ticket, Film, Home as HomeIcon, MessageCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

// Only the menu items that are actually wired up render here.
// (Hidden per spec: 회원/판매자/방송/상품/편성표/홈/카테고리/공지/광고/설정)
// Structured so `/admin/orders` and other future items can be inserted without touching pages.
const MENU = [
  { key: 'dashboard',    label: '관리자 대시보드',    to: '/admin',                     icon: LayoutDashboard },
  { key: 'applications', label: '판매자 신청 관리',    to: '/admin/seller-applications', icon: ClipboardList },
  { key: 'users',        label: '회원 관리',           to: '/admin/users',               icon: Users },
  { key: 'sellers',      label: '판매자 관리',         to: '/admin/sellers',             icon: Store },
  { key: 'broadcasts',   label: '방송 관리',           to: '/admin/broadcasts',          icon: Radio },
  { key: 'products',     label: '상품 관리',           to: '/admin/products',            icon: Package },
  { key: 'ads',          label: '광고 관리',           to: '/admin/ads',                 icon: Megaphone },
  { key: 'orders',       label: '주문 관리',           to: '/admin/orders',              icon: ShoppingCart },
  { key: 'payment-onboarding', label: '결제 서비스 신청', to: '/admin/payment-onboarding', icon: ShoppingCart },
  { key: 'cs-monitor',   label: 'CS 감시',              to: '/admin/inquiries',           icon: MessageCircle },
  { key: 'coupons',      label: '쿠폰 · 공동구매',      to: '/admin/coupons',             icon: Ticket },
  { key: 'short-clips',  label: '숏클립 승인 · 추천',    to: '/admin/short-clips',         icon: Film },
  { key: 'home-cms',     label: '홈 화면 관리',         to: '/admin/home',                icon: HomeIcon },
  { key: 'main-page',    label: '메인 홍보 콘텐츠',      to: '/admin/main-page',           icon: HomeIcon },
];

const roleLabel = (role) => (role === 'super_admin' ? '최고관리자' : '관리자');

const AdminLayout = ({ user, active, children, onLogout }) => {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/admin/me');
        if (!cancelled) setMeta(res.data);
      } catch (e) { /* silent — parent AdminRoute handles denial */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      if (onLogout) onLogout();
      navigate('/login');
    } catch (e) { /* silent */ }
  };

  const displayName = meta?.name || user?.name || '관리자';
  const displayEmail = meta?.email || user?.email || '';
  const displayRole = meta?.admin_role || (user?.role === 'super_admin' ? 'super_admin' : 'admin');
  const lastLogin = meta?.last_login_at ? format(new Date(meta.last_login_at), 'yyyy-MM-dd HH:mm', { locale: ko }) : '-';

  const SidebarInner = ({ onNav }) => (
    <nav className="flex flex-col gap-1 p-3" aria-label="Admin navigation">
      {MENU.map(({ key, label, to, icon: Icon }) => (
        <NavLink
          key={key}
          to={to}
          end={to === '/admin'}
          onClick={onNav}
          className={({ isActive }) =>
            `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              (active === key || isActive)
                ? 'bg-[#FF3B30] text-white shadow-sm'
                : 'text-gray-700 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'
            }`
          }
          data-testid={`admin-nav-${key}`}
        >
          <Icon size={16} /> {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0A0A0A]" data-testid="admin-layout">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/90 dark:bg-black/70 backdrop-blur border-b border-black/5 dark:border-white/10">
        <div className="flex items-center gap-3 px-4 sm:px-6 h-14">
          <button
            type="button"
            className="md:hidden p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/10"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="관리자 메뉴"
            data-testid="admin-menu-toggle"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>

          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-logo">
            <ShieldCheck size={18} className="text-[#FF3B30]" />
            <span className="text-lg font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="text-[#FF3B30]">LIVE</span>
              <span className="text-black dark:text-white">MOA</span>
              <span className="ml-2 text-xs font-bold text-gray-500">Admin</span>
            </span>
          </Link>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <span className="hidden sm:inline-flex items-center px-2 py-1 rounded-full bg-[#FF3B30]/10 text-[#FF3B30] text-[11px] font-bold" data-testid="admin-role-badge">
              {roleLabel(displayRole)}
            </span>
            <SafeImg src={meta?.picture || user?.picture} kind="avatar" alt={displayName} className="w-7 h-7 rounded-full object-cover" />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-xs font-bold" data-testid="admin-user-name">{displayName}</span>
              <span className="text-[10px] text-gray-500" data-testid="admin-user-email">{displayEmail}</span>
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-black/10 dark:border-white/10 rounded-full text-xs font-bold hover:border-[#FF3B30] hover:text-[#FF3B30]"
              data-testid="admin-back-to-site"
            >
              <ExternalLink size={12} /> <span className="hidden sm:inline">일반 사이트</span>
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-black/5 dark:bg-white/10 text-xs font-bold hover:bg-black/10 dark:hover:bg-white/20"
              data-testid="admin-logout"
            >
              <LogOut size={12} /> <span className="hidden sm:inline">로그아웃</span>
            </button>
          </div>
        </div>
        {/* Meta strip */}
        <div className="hidden md:flex items-center gap-4 px-6 py-1.5 text-[11px] text-gray-500 dark:text-gray-400 border-t border-black/5 dark:border-white/10 bg-white/60 dark:bg-black/40">
          <span>이메일: <span className="font-medium text-gray-700 dark:text-gray-200">{displayEmail}</span></span>
          <span>권한: <span className="font-medium text-gray-700 dark:text-gray-200">{roleLabel(displayRole)}</span></span>
          <span>마지막 로그인: <span className="font-medium text-gray-700 dark:text-gray-200">{lastLogin}</span></span>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden md:block w-60 shrink-0 border-r border-black/5 dark:border-white/10 bg-white dark:bg-[#0F0F0F] min-h-[calc(100vh-3.5rem)]" data-testid="admin-sidebar">
          <SidebarInner />
        </aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <>
            <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} />
            <aside
              className="fixed top-14 left-0 z-50 w-64 h-[calc(100vh-3.5rem)] bg-white dark:bg-[#0F0F0F] border-r border-black/5 dark:border-white/10 md:hidden animate-in slide-in-from-left"
              data-testid="admin-sidebar-mobile"
            >
              <SidebarInner onNav={() => setMobileOpen(false)} />
            </aside>
          </>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8" data-testid="admin-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
